<template>
  <section ref="feedSection" class="feed-section">
    <Banner class="feed-top-banner" v-if="$store.state.template.banners.enableBanners" :bannerSlot="'social_top_banner'" style="margin-bottom: -4px" />
    <div class="feed-middle-container">
      <SpinnerButton style="min-width: 40px; padding: 10px; margin: 0 auto; z-index: 1" v-if="showLoadMoreTopBtn" />
      <div class="feed-post-container" style="margin-top: 0" v-if="isAudiencePostingAllowed || userIsAdmin">
        <div class="feed-post-input" v-if="!useAsWidget">
          <img class="post-input-avatar" :src="user.picture" />
          <input class="post-input-textbox" :placeholder="$t('feed.postPlaceholder')" @click.prevent="openPostComposer" />
          <img class="post-input-avatar" style="margin: 0 0 0 10px; border-radius: 0" :src="activityImage" v-if="activityImage" />
        </div>
      </div>
      <transition-group appear type="transition" name="fade" mode="out-in">
        <Post
          class="feed-post-container"
          v-for="(post, k) in posts"
          :key="k + post.comment_id"
          :post="post"
          :activityName="activityName"
          :userIsAdmin="userIsAdmin"
          :isAudiencePostingAllowed="isAudiencePostingAllowed"
          :useAsWidget="useAsWidget"
          @editPost="openPostComposer"
          @postDeleted="postDeleted"
          @fixPost="fixPost"
          @unfixPost="unfixPost"
          @openComments="openComments"></Post>
      </transition-group>
      <SpinnerButton style="min-width: 40px; padding: 10px; margin: 0 auto; z-index: 1" v-if="showLoadMoreBtn" />
    </div>
    <PostComposer
      :key="'pc' + postComposerKey"
      :activityName="activityName"
      :showComposer="showPostComposer"
      :postId="editablePostId"
      :fatherId="editableFatherId"
      :post="editablePost"
      :enteredComment="enteredComment"
      :activityID="activityID"
      @closeClick="postComposerClosed" />
    <div class="new-post-btn-container">
      <button class="button new-post-btn" @click.prevent="openPostComposer"><ion-icon name="create-outline"></ion-icon></button>
    </div>
  </section>
</template>

<script>
import * as dayjs from 'dayjs';
import api from '@/services/api';
import Post from '@/components/user_content/Post.vue';
import PostComposer from '@/components/general/PostComposer.vue';
import { Hooper as MediaDisplay, Slide as MediaSlide, Navigation as MediaNavigation, Pagination as MediaPagination } from 'hooper';
import 'hooper/dist/hooper.css';
import DefaultAvatar from '@/assets/img/userSocialIconGeneric.png';
import EmojiPicker from 'vue-emoji-picker';
import VideoJSPlayer from '@/components/general/VideoJSPlayer.vue';
import Banner from '@/components/banners/Banner.vue';
import SpinnerButton from '@/components/ui/SpinnerButton.vue';

export default {
  name: 'Feed',

  props: {
    activityID: { type: String, default: '', required: false },
    activityName: { type: String, default: '', required: false },
    activityImage: { type: String, default: '', required: false },
    commentRoomId: { type: String, default: '', required: false },
    userId: { type: String, default: '', required: false },
    highlights: { type: Array, default: null, required: false },
    isAudiencePostingAllowed: { type: Boolean, default: true, required: false },
    useAsWidget: { type: Boolean, default: false, required: false },
  },
  components: { PostComposer, MediaDisplay, MediaSlide, MediaNavigation, MediaPagination, EmojiPicker, VideoJSPlayer, Banner, Post, SpinnerButton },

  directives: {
    focus: {
      inserted: function (el) {
        el.focus();
      },
    },
  },

  data() {
    return {
      busy: false,
      readMoreActivated: false,
      postComposerKey: 0,
      commentsKey: 0,
      showPostComposer: false,
      dayjs: null,
      editablePostId: null,
      editableFatherId: undefined,
      editablePost: null,
      currentLastId: '',
      currentLastCommentId: '',
      media: { images: [] },
      editActions: [],
      enteredComment: '',
      postsRaw: [],
      emojiIconFill: 'white',
      message: '',
      input: '',
      search: '',
      authorizationData: {},
      showLoadMoreBtn: false,
      showLoadMoreTopBtn: false,
      reachedEndOfFeed: false,
      userIsAdmin: false,
      debounceTimer: null,
      debounceTimeInMs: 500,
      autoScrollInterval: null,
      autoScrollState: {
        currentPostIndex: 0,
        isScrolling: false,
        isPaused: false,
        startTime: 0,
        pauseStartTime: 0,
        scrollStartPosition: 0,
        scrollTargetPosition: 0,
        animationFrame: null,
      },
    };
  },

  computed: {
    posts() {
      const highlighted = this.postsRaw.filter((post) => post.highlight);
      const nonHighlighted = this.postsRaw.filter((post) => !post.highlight);
      return [...highlighted, ...nonHighlighted];
    },
    highlightedPosts() {
      return this.posts.filter((post) => post.highlight);
    },
    nonHighlightedPosts() {
      return this.posts.filter((post) => !post.highlight);
    },
    editActionsComputed() {
      return [
        { action: this.$t('post.actions.edit'), icon: 'create-outline', method: 'edit', needsAuthorization: false },
        { action: this.$t('post.actions.delete'), icon: 'trash-outline', method: 'delete', needsAuthorization: false },
        { action: this.$t('post.actions.pin'), icon: 'attach-outline', method: 'fix', needsAuthorization: true },
        { action: this.$t('post.actions.unpin'), icon: 'browsers-outline', method: 'unfix', needsAuthorization: true },
      ];
    },
  },
  methods: {
    applyWidgetStyles() {
      const styleId = 'feed-widget-styles';

      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }

      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        .feed-post-container {
         border-radius: 8px !important;
        }
        .post-top {
          border-radius: 8px 8px 0 0 !important;
        }
        .post-bottom {
          border-radius: 0 0 8px 8px !important;
        }
        .post-avatar {
          width: 20px !important;
          height: 20px !important;
        }
        .post-user {
          font-size: 10px !important;
          max-width: 100px !important;
        }
        .post-time {
          font-size: 6px !important;
        }
        .post-middle-text {
          font-size: 10px !important;
          line-height: 1.5 !important;
        }

        .feed-section::-webkit-scrollbar {
          width: 0 !important;
        }
      `;

      document.head.appendChild(style);
    },
    getPostsOnScrollTop() {
      this.currentLastId = '';
      this.reachedEndOfFeed = false;

      const feedSection = this.$refs.feedSection;
      if (feedSection.scrollTop === 0) {
        let comm = `event/${this.$store.state.eventId}/comment`;
        if (this.activityID) {
          comm = `event/${this.$store.state.eventId}/comment/activity/${this.activityID}`;
        }
        api
          .get(comm)
          .then((response) => {
            if (!response.data.length) return;
            if (response.data) {
              this.postsRaw = [];
              response.data.map((item) => {
                try {
                  item.media = JSON.parse(item.media) ?? {};
                } catch (error) {
                  item.media = {};
                }
                try {
                  item.obj = JSON.parse(item.reactions);
                  const reaction = item.obj.map(({ reaction, reaction_count }) => ({ [reaction]: reaction_count }));
                  reaction.map((info) => Object.assign(item, info));
                } catch (error) {
                  item.like = 0;
                }
                try {
                  item.obj = JSON.parse(item.my_reactions);
                  const my_reaction = item.obj.map(({ reaction, reaction_count }) => ({ [reaction]: reaction_count }));
                  item.my_reaction = my_reaction;
                  item.i_reacted = item.my_reactions_count > 0;
                } catch (error) {
                  item.i_reacted = false;
                }

                item.readmoreActive = false;
                item.showComment = false;
                item.showImage = item.media.length > 0;
                item.showEditPanel = false;
                item.avatar = item.avatar ? item.avatar : DefaultAvatar;
                this.postsRaw = [...this.postsRaw, item];
              });
              let lastPost = this.postsRaw?.slice(-1)[0];
              this.currentLastId = lastPost?.comment_id;
            }
          })
          .catch((err) => {
            console.error('error', err);
          });
      }
    },
    getPostsDebounced() {
      clearTimeout(this.debounceTimer);

      const feedSection = this.$refs.feedSection;
      if (!feedSection) return;

      const isScrollable = feedSection.scrollHeight > feedSection.clientHeight;
      const isAtBottom = feedSection.scrollTop + feedSection.clientHeight >= feedSection.scrollHeight - 2;
      const isAtTop = feedSection.scrollTop === 0;

      if (isAtTop) {
        this.triggerRubberBandTopEffect(feedSection);
      }

      this.debounceTimer = setTimeout(() => {
        if (isAtTop) {
          this.getPostsOnScrollTop();
          return;
        }

        if (isScrollable && isAtBottom && !this.reachedEndOfFeed) {
          this.triggerRubberBandBottomEffect(feedSection);
          this.getPosts();
          return;
        }
      }, this.debounceTimeInMs);
    },
    triggerRubberBandTopEffect(feedSection) {
      if (!this._rubberBanding) {
        this._rubberBanding = true;
        feedSection.style.transition = 'padding-top 0.18s cubic-bezier(0.23, 1, 0.32, 1)';
        feedSection.style.paddingTop = '60px';
        this.showLoadMoreTopBtn = true;
        setTimeout(() => {
          feedSection.style.paddingTop = '0px';
          setTimeout(() => {
            feedSection.style.transition = '';
            this._rubberBanding = false;
            this.showLoadMoreTopBtn = false;
          }, 180);
        }, 200);
      }
    },
    triggerRubberBandBottomEffect(feedSection) {
      if (!this._rubberBanding) {
        this._rubberBanding = true;
        feedSection.style.transition = 'padding-bottom 0.18s cubic-bezier(0.23, 1, 0.32, 1)';
        feedSection.style.paddingBottom = '60px';
        this.showLoadMoreBtn = true;
        setTimeout(() => {
          feedSection.style.paddingBottom = '0px';
          setTimeout(() => {
            feedSection.style.transition = '';
            this._rubberBanding = false;
          }, 180);
        }, 200);
      }
    },
    getPosts() {
      const feedSection = this.$refs.feedSection;
      let comm = `event/${this.$store.state.eventId}/comment`;
      if (this.activityID) {
        comm = `event/${this.$store.state.eventId}/comment/activity/${this.activityID}`;
      } else if (this.userId) {
        comm = `event/${this.$store.state.eventId}/comment/user/${this.userId}`;
      }
      if (this.currentLastId) {
        comm += `?last=${this.currentLastId}`;
      }
      this.showLoadMoreBtn = true;
      feedSection.scrollTop = feedSection.scrollTop - 1;
      api
        .get(comm)
        .then((response) => {
          if (!response.data.length) {
            this.showLoadMoreBtn = false;
            this.reachedEndOfFeed = true;
            return;
          }
          let postsRead = response.data;
          if (postsRead) {
            postsRead.map((item) => {
              try {
                item.media = JSON.parse(item.media) ?? {};
              } catch (error) {
                item.media = {};
              }
              try {
                item.obj = JSON.parse(item.reactions);
                const reaction = item.obj.map(({ reaction, reaction_count }) => ({ [reaction]: reaction_count }));
                reaction.map((info) => Object.assign(item, info));
              } catch (error) {
                item.like = 0;
              }
              try {
                item.obj = JSON.parse(item.my_reactions);
                const my_reaction = item.obj.map(({ reaction, reaction_count }) => ({ [reaction]: reaction_count }));
                item.my_reaction = my_reaction;
                item.i_reacted = item.my_reactions_count > 0;
              } catch (error) {
                item.i_reacted = false;
              }
              item.readmoreActive = false;
              item.showComment = false;
              item.showImage = item.media.length > 0;
              item.showEditPanel = false;
              item.avatar = item.avatar ? item.avatar : DefaultAvatar;
              this.postsRaw = [...this.postsRaw, item];
            });
            let lastPost = this.posts.slice(-1)[0];
            this.currentLastId = lastPost.comment_id;
          }
        })
        .catch((err, data) => {
          console.error('error', err.response, data);
        });
    },
    fixPost(postId) {
      if (postId) {
        api
          .put(`event/${this.$store.state.eventId}/comment/${postId}`, {
            highlight: true,
          })
          .then((response) => {
            this.$emit('fixedPost', postId);
            this.$refs.feedSection.scrollTop = 0;
            // console.log(response);
          })
          .catch((err, data) => {
            console.error('error', err.response, data);
            alert('Falha ao fixar post');
          });
      }
    },
    unfixPost(postId) {
      if (postId) {
        api
          .put(`event/${this.$store.state.eventId}/comment/${postId}`, {
            highlight: false,
          })
          .then((response) => {
            this.$emit('unfixedPost', postId);
            this.$refs.feedSection.scrollTop = 1;
            this.$nextTick(() => {
              this.$refs.feedSection.scrollTop = 0;
            });
          })
          .catch((err, data) => {
            console.error('error', err.response, data);
            alert('Falha ao soltar post');
          });
      }
    },
    postDeleted(postId) {
      this.postsRaw = this.postsRaw.filter((item) => item.comment_id !== postId);
    },
    postComposerClosed() {
      this.showPostComposer = false;
      this.editablePostId = null;

      if (this.editableFatherId) {
        this.editableFatherId = null;
      }
      this.$refs.feedSection.scrollTop = 10;
      this.$nextTick(() => {
        this.$refs.feedSection.scrollTop = 0;
        this.getPostsOnScrollTop();
      });
    },
    openPostComposer(post) {
      this.$refs.feedSection.scrollTop = 100;
      this.showPostComposer = true;

      if (post) {
        if (post.father_id) this.editableFatherId = post.father_id;
        this.editablePost = post;
        this.editablePostId = post.comment_id;
        this.enteredComment = post.message;
        this.postComposerKey = this.$RNG.randomKey();
      }
    },
    openComments(post) {
      this.$emit('openComments', post);
    },
    getLevelsData() {
      if (this.$store.state.eventId == '74581de0-4c3d-11eb-8ce9-353a355b297f') {
        // console.log('getLevelsData - EVENTO ACHE');
        api
          .get(`activity/13c4fd90-6268-11eb-9bb9-e183bd803493`)
          .then((response) => {
            //console.log(response);
            this.authorizationData = { ...response.data };
            this.authorizationData.property = response.data.property ? JSON.parse(response.data.property) : {};
            let levels = this.authorizationData.property.levels ? this.authorizationData.property.levels : [];
            try {
              levels.map((level) => {
                // console.log('getLevelsData - user level: ' + level);
                // console.log('level: ' + JSON.stringify(level));
                if (level.name == 'adminFeed') {
                  level.level.map((role) => {
                    if (this.$store.state.user.role == role) {
                      // console.log('role: ' + role);
                      this.userIsAdmin = true;
                    }
                  });
                }
              });
            } catch (err) {
              console.error('Could not get levels ', err);
            }
          })
          .catch((err) => {
            console.error(err);
          });
      }
    },
    startAutoScroll() {
      // Enhanced auto-scroll with elegant curves and timed pauses
      const feedSection = this.$refs.feedSection;
      if (!feedSection) return;

      // Reset scroll to top
      feedSection.scrollTop = 0;

      // Clear any existing intervals or animation frames
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
      }
      if (this.autoScrollState.animationFrame) {
        cancelAnimationFrame(this.autoScrollState.animationFrame);
      }

      // Reset auto-scroll state
      this.autoScrollState = {
        currentPostIndex: 0,
        isScrolling: false,
        isPaused: false,
        startTime: 0,
        pauseStartTime: 0,
        scrollStartPosition: 0,
        scrollTargetPosition: 0,
        animationFrame: null,
      };

      this.performAutoScroll();
    },
    performAutoScroll() {
      const feedSection = this.$refs.feedSection;
      if (!feedSection) return;

      const posts = feedSection.querySelectorAll('.feed-post-container');
      if (posts.length === 0) return;

      const state = this.autoScrollState;
      const currentTime = performance.now();

      if (!state.isScrolling && !state.isPaused) {
        // Start scrolling to next post
        if (state.currentPostIndex >= posts.length) {
          // Reset to beginning
          state.currentPostIndex = 0;
          feedSection.scrollTop = 0;
          state.scrollStartPosition = 0;
        }

        const targetPost = posts[state.currentPostIndex];
        const postRect = targetPost.getBoundingClientRect();
        const feedRect = feedSection.getBoundingClientRect();

        // Calculate target scroll position (center the post in view)
        const targetScrollTop = feedSection.scrollTop + postRect.top - feedRect.top - (feedRect.height - postRect.height) / 2;

        state.scrollStartPosition = feedSection.scrollTop;
        state.scrollTargetPosition = Math.max(0, Math.min(targetScrollTop, feedSection.scrollHeight - feedSection.clientHeight));
        state.startTime = currentTime;
        state.isScrolling = true;
      }

      if (state.isScrolling) {
        // Smooth scroll with easing curve
        const scrollDuration = 2000; // 2 seconds to scroll to next post
        const elapsed = currentTime - state.startTime;
        const progress = Math.min(elapsed / scrollDuration, 1);

        // Easing function: easeInOutCubic for elegant curve
        const easeInOutCubic = (t) => {
          return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        };

        const easedProgress = easeInOutCubic(progress);
        const currentScrollTop = state.scrollStartPosition + (state.scrollTargetPosition - state.scrollStartPosition) * easedProgress;

        feedSection.scrollTop = currentScrollTop;

        if (progress >= 1) {
          // Scrolling complete, start pause
          state.isScrolling = false;
          state.isPaused = true;
          state.pauseStartTime = currentTime;
          state.currentPostIndex++;
        }
      }

      if (state.isPaused) {
        // Pause for 3 seconds to display the post
        const pauseDuration = 3000; // 3 seconds
        const pauseElapsed = currentTime - state.pauseStartTime;

        if (pauseElapsed >= pauseDuration) {
          state.isPaused = false;
        }
      }

      // Continue the animation loop
      state.animationFrame = requestAnimationFrame(() => this.performAutoScroll());
    },
  },
  beforeMount() {
    this.dayjs = dayjs;
    this.user = this.$store.state.user;
    this.chat = this.$store.state.template.chat;
    this.emojiIconFill = this.chat.inputTextColor;
    this.userIsAdmin = this.$store.state.user.role == '1819bea8-7b46-11ea-bc55-0242ac130003' || this.$store.state.user.role == '1819bd5e-7b46-11ea-bc55-0242ac130003';

    this.getLevelsData();
  },
  mounted() {
    this.getPostsOnScrollTop();
    if (this.useAsWidget) {
      this.applyWidgetStyles();
      this.startAutoScroll();
    } else {
      this.$refs.feedSection.addEventListener('scroll', this.getPostsDebounced);
    }
  },
  beforeDestroy() {
    this.$refs.feedSection.removeEventListener('scroll', this.getPostsDebounced);

    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
    if (this.autoScrollState.animationFrame) {
      cancelAnimationFrame(this.autoScrollState.animationFrame);
      this.autoScrollState.animationFrame = null;
    }

    // Clean up widget styles if they were applied
    const existingStyle = document.getElementById('feed-widget-styles');
    if (existingStyle) {
      existingStyle.remove();
    }
  },
};
</script>

<style lang="scss">
.feed-section {
  position: relative;
  flex-grow: 1;
  max-width: 640px;
  height: 100%;
  margin: 0;
  padding: 0;
  margin-right: 2%;
  padding-right: 1%;

  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 10px;
    @include media('<=phone-plus') {
      width: 0;
    }
  }

  &::-webkit-scrollbar-track {
    background: var(--scrollbar-bgColor) !important;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--scrollbar-handleColor) !important;
  }

  @include media('<=tablet') {
    display: flex;
    padding: 0px 20px 5%;
    margin-right: 0;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
  }
  @include media('<=phone-plus') {
    padding: 10px 20px 5%;
  }
  @include media('<=phone') {
    padding: 0;
  }

  .feed-top-banner {
    padding: 0 0 0 5%;

    @include media('<=tablet') {
      padding: 0 0 0 0;
    }
  }
}
.feed-middle-container {
  position: relative;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-content: stretch;

  @include media('<=phone') {
    padding: 0 10px;
  }
}
.feed-post-input {
  user-select: none;
  position: sticky;
  background: var(--feedSocial-bgColor);
  width: 100%;
  padding: 3%;
  border-radius: var(--feedSocial-borderRadius);

  display: flex;
  // flex-wrap: wrap;
  justify-content: center;
}
.feed-post-container {
  position: relative;
  width: 100%;
  margin: 10px 0 4px;
  padding: 0 0 0 5%;
  color: var(--feedSocial-textColor);
  border-radius: var(--feedSocial-postBorderRadius, 10px);
  font-size: 1rem;

  display: flex;
  flex-direction: column;
  justify-content: stretch;
  align-items: center;

  @include media('<=tablet') {
    padding: 0;
  }
  @include media('<=phone-plus') {
    width: 100%;
  }
}
.feed-group-banner {
  position: relative;
  margin-bottom: 20px;
  width: 100%;
  max-height: 380px;
  object-fit: cover;
  border-radius: var(--feedSocial-borderRadius);
}
.post-top {
  position: relative;
  width: 100%;
  max-height: 80px;
  background-color: var(--feedSocial-bgColor);
  display: flex;
  justify-content: space-between;
  padding: 3% 3% 2% 3%;
  border-top-left-radius: var(--feedSocial-borderRadius);
  border-top-right-radius: var(--feedSocial-borderRadius);
  user-select: none; /* supported by Chrome and Opera */
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  .post-avatar {
    position: relative;
    object-fit: cover;
    object-position: center;
    background-color: transparent;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    margin-right: 2%;
  }

  .post-user-and-time {
    position: relative;
    margin-right: auto;

    .post-user {
      color: var(--feedSocial-textColor);
      width: 100%;
      position: relative;
      font-size: 1rem;

      max-width: 29vw;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      @media (max-width: 1200px) {
        max-width: 20vw;
      }

      @include media('<=phone-plus') {
        max-width: 60vw;
      }
    }

    .post-group-and-time {
      display: flex;
      align-items: center;
      gap: 4px;

      .group-name {
        margin: 2px 0 0 0;
        font-size: 0.7rem;
        color: var(--feedSocial-subColor);

        max-width: 10vw;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      .post-time {
        margin: 0 0 0 2px;
        font-size: 0.6rem;
        color: var(--feedSocial-subColor);
        opacity: 0.8;
      }
    }
  }

  .post-edit-icon {
    cursor: pointer;
    width: 14px;
    height: 14px;
    padding: 1%;
    color: var(--feedSocial-textColor);
    opacity: 0.5;

    &:hover {
      color: white;
      background-color: var(--feedSocial-subColor);
      border-radius: 50%;
      opacity: 0.3;
    }
  }
}
.pinned-icon {
  // cursor: pointer;
  font-size: 1.4rem;
  padding: 1%;
  color: var(--feedSocial-textColor);
  opacity: 0.5;
}

.post-user-overlay-container,
.comment-user-overlay-container {
  position: absolute;
  top: -10px;
  left: 0;
  max-width: 220px;
  padding: 6px 6px 0;
  z-index: 1;
  background: var(--feedSocial-bgColor);
  border: 1px solid var(--feedSocial-subColor);
  border-radius: var(--feedSocial-borderRadius);
  box-shadow: 0 2px 2px 0 rgba(240, 240, 240, 0.2);
  font-size: 1rem;

  display: none;
  align-items: flex-start;
  justify-content: flex-start;

  opacity: 0; // opacity: 0;
  transition: 0.5s opacity;
  .overlay-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 10px;
  }
  &:hover {
    opacity: 1;
  }
}
.overlay-items-container {
  position: relative;
  z-index: 1;

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;

  .post-user-msg-btn {
    color: var(--feedSocial-postButtonTextColor);
    background: var(--feedSocial-postButtonBgColor);
  }
}
.post-user-overlay-item {
  position: relative;
  color: var(--feedSocial-textColor);

  ion-icon {
    margin-right: 8px;
  }
}
.isComment {
  top: 38px !important;
}
.post-edit-panel {
  position: absolute;
  top: 40px;
  right: 0.3vw;
  z-index: 20;
  // min-width: 38%;
  color: var(--feedSocial-textColor);
  background-color: var(--feedSocial-bgColor);
  border: 1px solid var(--feedSocial-subColor);
  border-radius: var(--feedSocial-borderRadius);
  box-shadow: #2223 0px 0px 10px;
  padding: 10px;
  display: flex;
  opacity: 1;
  flex-direction: column;
  justify-content: center;
  justify-items: center;
  align-items: flex-start;
  user-select: none;
  transition: all 0.2s ease-in-out;

  .post-edit-action {
    cursor: pointer;
    position: relative;
    width: 100%;
    font-size: 1rem;
    padding: 4px;

    display: flex;
    align-items: center;
    justify-content: flex-start;
    .edit-action-container {
      min-width: 90%;
      white-space: nowrap;
    }
    &:hover {
      background-color: #aaaaaa30;
      border-radius: 4px;
      opacity: 1;
    }
  }
}

.post-middle {
  position: relative;
  width: 100%;
  height: 100%;
  font-size: 1rem;
  color: var(--feedSocial-textColor);
  background: var(--feedSocial-bgColor);
  display: flex;
  flex-direction: column;
  padding: 0% 6px;

  .post-middle-text {
    position: relative;
    // font-family: 'SegoeUI', 'Roboto', 'Lato', 'Helvetica', 'Arial', sans-serif;
    // font-weight: 200;
    letter-spacing: 0.06rem;
    line-height: 1.1;
    padding: 2% 3% 3% 3%;
    word-wrap: break-word;
    .post-readmore-button {
      position: absolute;
      bottom: 0;
      left: 0;
      margin: 0;
      background-color: #00000000;
      width: 100%;
      height: 20%;
      display: flex;
      justify-content: center;
      padding: 1%;
    }
  }
  .media-display-container {
    position: relative;
    height: 100%;
    width: 100%;
    user-select: none; /* supported by Chrome and Opera */
    -webkit-user-select: none; /* Safari */
    -moz-user-select: none; /* Firefox */
  }
}
.parsed-box-media-container {
  position: relative;
  user-select: none; /* supported by Chrome and Opera */
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
}
.parsed-box-image {
  position: relative;
  object-fit: cover;
  width: 100%;
  max-height: 360px;
  // margin-bottom: -3px;
}
.parsed-box-container {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 0 2% 1%;
  background-color: var(--input-bg);
  .parsed-title {
    color: var(--feedSocial-textColor);
    font-size: 1rem;
    // opacity: 0.9;
  }
  .parsed-site {
    color: var(--feedSocial-textColor);
    font-size: 0.8rem;
    opacity: 0.7;
    margin: 1% 0;
  }
}
.post-bottom {
  position: relative;
  width: 100%;
  height: 100%;
  background-color: var(--feedSocial-bgColor);
  color: var(--feedSocial-subColor);
  display: flex;
  flex-direction: column;
  padding: 1.5% 3%;
  border-bottom-left-radius: var(--feedSocial-borderRadius);
  border-bottom-right-radius: var(--feedSocial-borderRadius);
  user-select: none;
  .post-bottom-reactions {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 1rem;
    margin-bottom: 10px;
    user-select: none; /* supported by Chrome and Opera */
    -webkit-user-select: none; /* Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */

    .post-bottom-reactions-values {
      cursor: pointer;
      position: relative;
      text-align: right;
      font-size: 0.8rem;
      display: flex;

      &:hover {
        text-decoration: underline;
      }
      &:first-child {
        &:hover {
          cursor: auto;
          font-size: 1.4rem;
          text-decoration: none;
        }
      }
    }
  }

  .post-bottom-actions {
    cursor: pointer;
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-around;
    margin-bottom: 6px;
    .post-bottom-action-btn {
      display: flex;
      justify-content: center;
      width: 50%;
      padding: 1% 0 1% 0;
      margin: 2% 0 0 0;
      font-size: 1rem;
      a {
        color: var(--feedSocial-subColor);
        display: flex;
      }
      &:hover {
        transform: scale(0.94);
      }
      &:active {
        transform: scale(0.94);
      }
    }
  }
}
.post-actions-icon {
  position: relative;
  font-size: 1.5rem;
  margin-right: 10px;
  min-width: 20%;
}
.post-bottom-separator {
  position: relative;
  height: 1px;
  width: 100%;
  margin: 0px 0 0px 0;
  background: var(--feedSocial-postInputOutlineColor);
}
.post-comments-container {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding-top: 1%;
  .post-comment-input-container {
    position: relative;
    width: 100%;
    height: 90px;
    padding: 2% 0;
    display: flex;
    align-items: center;
    .comment-input-textbox {
      position: relative;
      width: 100%;
      height: 80%;
      border-radius: var(--feedSocial-borderRadius);
      border: 1px solid var(--feedSocial-postInputOutlineColor);
      outline: none;
      color: var(--feedSocial-textColor);
      background-color: var(--feedSocial-bgColor);
      padding: 2%;
      &::placeholder {
        color: var(--feedSocial-textColor);
        opacity: 0.7;
      }

      @include media('<=phone-plus') {
        width: 100%;
        height: auto;
        padding: 4% 68px 4% 4%;
      }
    }
  }
  .post-comments-comment {
    position: relative;
    display: flex;
    justify-content: stretch;
    align-items: flex-start;
    padding: 1%;

    .comment-texts {
      position: relative;
      // backdrop-filter: brightness(0.9);
      border-radius: var(--feedSocial-borderRadius);
      padding: 2%;
      display: flex;
      flex-wrap: wrap;
      width: 90%;
      justify-content: flex-start;
      white-space: pre-line;
      user-select: text;
      .comment-user {
        color: var(--feedSocial-textColor);
        opacity: 0.8;
        font-size: 1rem;
        font-weight: 100;
        margin-right: 1%;
        margin-bottom: 1.3%;
        // ellipsis on text overflow
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .comment-time {
        color: var(--feedSocial-subColor);
        font-size: 0.7rem;
        margin-top: 4px;
      }
      .comment-edit-icon {
        cursor: pointer;
        position: relative;
        font-size: 0.9rem;
        padding: 1%;
        margin-left: auto;

        &:hover {
          background-color: #aaaaaa30;
          border-radius: 50%;
        }
      }
      .comment-content {
        color: var(--feedSocial-textColor);
        // opacity: 0.7;
        font-size: 0.9rem;
        position: relative;
        width: 99%;
        word-wrap: break-word;
        white-space: pre-line;
        margin-bottom: 8px;
      }
    }
  }
}

.comment-avatar {
  position: relative;
  width: 10%;
  margin-right: 10px;
  // background-color: red;
  // display: flex;
  // justify-content: center;

  img {
    position: relative;
    object-fit: cover;
    object-position: center;
    background-color: transparent;
    width: 40px;
    height: 40px;
    border-radius: 50%;
  }
}
.comment-user-overlay-container {
  .overlay-avatar {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 10px;
  }
  &:hover {
    cursor: pointer;
    visibility: visible;
    opacity: 1;
  }
}
.comment-avatar:hover ~ .comment-user-overlay-container {
  visibility: visible;
  opacity: 1;
}
.comment-parsed-box-media-container {
  position: relative;
  width: 100%;
}
.comment-like-container {
  cursor: pointer;
  position: relative;
  margin-top: 8px;
  // width: 32px;
  // height: 32px;
  font-size: 1rem;
  // z-index: 1;
  display: flex;
  align-items: center;
  user-select: none;

  .comment-like-icon {
    position: relative;
    padding: 4px 4px 0px 4px;
    color: var(--feedSocial-textColor);
    background-color: var(--feedSocial-bgColor);
    opacity: 0.7;
    border-radius: 50%;
    margin-right: 6px;
    &:hover {
      color: var(--feedSocial-bgColor);
      background-color: var(--feedSocial-subColor);
    }
  }
  .comment-like-text {
    font-size: 0.7rem;
  }
}
.binaryLiked {
  color: var(--feedSocial-bgColor);
  background-color: var(--feedSocial-bgColor);
}
.post-input-avatar {
  position: relative;
  object-fit: cover;
  object-position: center;
  background-color: transparent;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 2%;
  user-select: none;
}
.post-input-textbox {
  position: relative;
  flex-grow: 1;
  border-radius: var(--feedSocial-borderRadius);
  border: 1px solid var(--feedSocial-postInputOutlineColor);
  outline: none;
  color: var(--input-text);
  background-color: var(--input-bg);

  padding: 2%;
  &::placeholder {
    color: var(--feedSocial-textColor);
    opacity: 0.7;
    padding-left: 10px;
  }
}
.post-image-panel {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  background-color: black;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20;
  padding: 110px 0% 4%;

  @include media('<=tablet') {
    padding: 0;
  }

  .post-image-fullscreen-gallery {
    position: relative;
    height: 100%;
    width: 100%;
    // margin-top: 100px;
    .post-image-fullscreen {
      position: relative;
      object-fit: contain;
      height: 100%;
      width: 100%;
      z-index: 1;
    }
  }
}
.post-image-fullscreen-close-btn {
  cursor: pointer;
  position: fixed;
  top: 100px;
  right: 2%;
  width: 100px;
  height: 100px;
  font-size: 60px;
  z-index: 100;
  color: var(--feedSocial-subColor);
  display: flex;
  justify-content: flex-end;
}
:focus {
  outline: none;
}
.post-image {
  cursor: pointer;
  position: relative;
  object-fit: contain;
  object-position: center;
  max-height: 340px;
  width: 100%;
  z-index: 1;
}
.post-video {
  width: 100%;
  min-height: 360px;
  max-height: 420px;
}

.hooper-slide {
  width: 100%;
  justify-content: center;
}

.hooper-list:hover .hooper-next {
  display: block;
  background-color: #eeeeee40;
  border-radius: 50%;
  transform: scale(0.8) translateY(-40%);
  margin: 0 2%;
}
.hooper-list:hover .hooper-prev {
  display: block;
  background-color: #eeeeee60;
  border-radius: 50%;
  transform: scale(0.8) translateY(-40%);
  margin: 0 2%;
}
.hooper-next,
.hooper-prev {
  display: none;
}
.hooper-indicator {
  height: 10px;
  width: 10px;
  background-color: #bbbbbb80;
}
.hooper-indicator.is-active {
  background-color: #88888875;
}

// EMOJI PICKER
.comments-emoji-picker-wrapper {
  position: absolute;
  top: 32px;
  width: 100%;
  display: inline-block;
  // z-index: 1;
}
.comments-emoji-invoker {
  position: absolute;
  top: 0;
  right: 40px;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  // z-index: 10;
}
.comment-emoji-picker {
  top: -336px;
  position: absolute;
  z-index: 1;
  border: 1px solid #ffffff80;
  width: 100%;
  height: 320px;
  overflow: auto;
  overflow-x: hidden;
  padding: 1rem;
  box-sizing: border-box;
  border-radius: var(--feedSocial-borderRadius);
  background: var(--feedSocial-bgColor);
  padding-right: 8px;
  &::-webkit-scrollbar {
    background-color: transparent;
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background-color: var(--chat-scrollbarBGColor);
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--chat-scrollbarHandleColor);
  }
}
.comment-emoji-picker h5 {
  margin: 0;
  margin-top: 10px;
  color: var(--chat-textColor);
  text-transform: uppercase;
  font-size: 1rem;
  cursor: default;
}
.comments-emoji-invoker:hover {
  transform: scale(1.1);
}
.comments-emoji-invoker > svg {
  fill: var(--feedSocial-textColor);
  opacity: 0.5;
}
.emoji-picker__search {
  position: relative;
  display: flex;
  height: 24px;
}
.emoji-picker__search > input {
  flex: 1;
  border-radius: 12px;
  border: 1px solid #ccc;
  padding: 0.5rem 1rem;
  outline: none;
  width: 90%;
}
.emoji-picker .emojis {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}
.emoji-picker .emojis:after {
  content: '';
  flex: auto;
}
.emoji-picker .emojis span {
  padding: 0.2rem;
  cursor: pointer;
  border-radius: 5px;
}
.emoji-picker .emojis span:hover {
  background: #ececec;
  cursor: pointer;
}
.emojis {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  span {
    padding: 4px;
    cursor: pointer;
  }
}
.new-post-btn-container {
  @include media('>phone-plus') {
    display: none;
  }
  position: fixed;
  right: 8px;
  bottom: 34px;
  margin-left: auto;

  display: none;
  align-items: center;
  justify-content: center;
  .new-post-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    font-size: 1.5rem;
    background: var(--feedSocial-postButtonBgColor);
    color: var(--feedSocial-postButtonTextColor);
    border: none;
  }
}
@include media('<=phone-plus') {
  .feed-right-container {
    display: none;
  }
  .feed-middle-container {
    width: 100%;
  }

  .post-bottom {
    padding-bottom: 3%;
  }
  .post-image {
    // object-fit: cover;
    max-height: 42vw;
  }
  .post-video {
    max-height: 42vw;
  }
  .comment-avatar {
    display: flex;
    justify-content: center;
    img {
      height: 56px;
      width: 56px;
    }
  }
  .post-comments-comment {
    justify-content: flex-end;
    align-items: flex-start;
    padding: 1%;
  }
}
@include media('<=phone') {
  .feed-post-container {
    padding: 2% 0 1% 0;
    min-width: unset;
  }
  // .post-top {
  //   padding: 5% 3% 1%;
  // }
  .post-bottom {
    padding: 5% 0;
  }
  .post-bottom-reactions-values {
    margin: 0 4%;
  }
  .post-bottom-action-btn {
    margin-top: 20px !important;
  }
  .post-comment-input-container {
    margin-left: 1%;
    max-width: 90vw;
    height: auto;
    padding: 1% 0;
  }
  .post-input-avatar {
    width: 30px;
    height: 30px;
    margin-right: 4px;
  }
  .comment-input-textbox {
    font-size: 0.9rem;
    height: 10px;
    padding: 0;
  }
  .post-user-overlay-container,
  .comment-user-overlay-container {
    width: 100%;
  }
  .comment-avatar img {
    height: 30px;
    width: 30px;
  }
  .comment-user {
    font-size: 1rem !important;
  }
  .comment-time {
    font-size: 0.7rem !important;
  }
  .comment-texts {
    margin-right: 6px;
  }
  .comment-content {
    font-size: 1rem;
  }
  .post-image {
    // object-fit: cover;
    max-height: 62vw;
  }
  .post-video {
    min-height: 62vw;
  }
}
</style>
